// src/pages/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  User, 
  Phone, 
  Globe, 
  MapPin, 
  Building2, 
  Save, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  Camera
} from 'lucide-react';
import { RoleInvitationsPanel } from '../components/shared/RoleInvitationsPanel';

export const ProfilePage = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    photoURL: '',
    mobileNumber: '',
    country: '',
    province: '',
    city: '',
    organisationIds: []
  });

  const [orgInput, setOrgInput] = useState('');

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const userRef = doc(db, 'pb-users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        setFormData({
          displayName: data.displayName || '',
          email: data.email || '',
          photoURL: data.photoURL || '',
          mobileNumber: data.mobileNumber || '',
          country: data.country || '',
          province: data.province || '',
          city: data.city || '',
          organisationIds: data.organisationIds || []
        });
      }
    } catch (err) {
      setError('Failed to load profile');
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess(false);
    setError(null);
  };

  const handleAddOrganisation = () => {
    if (orgInput.trim() && !formData.organisationIds.includes(orgInput.trim())) {
      setFormData(prev => ({
        ...prev,
        organisationIds: [...prev.organisationIds, orgInput.trim()]
      }));
      setOrgInput('');
    }
  };

  const handleRemoveOrganisation = (orgId) => {
    setFormData(prev => ({
      ...prev,
      organisationIds: prev.organisationIds.filter(id => id !== orgId)
    }));
  };

  const handleSave = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const userRef = doc(db, 'pb-users', user.uid);
      await updateDoc(userRef, {
        mobileNumber: formData.mobileNumber,
        country: formData.country,
        province: formData.province,
        city: formData.city,
        organisationIds: formData.organisationIds,
        updatedAt: serverTimestamp()
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="relative group">
              <img
                src={formData.photoURL || '/default-avatar.png'}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover ring-4 ring-blue-100"
              />
              <button className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="w-6 h-6 text-white" />
              </button>
            </div>
            
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {formData.displayName}
              </h1>
              <p className="text-slate-600 flex items-center justify-center sm:justify-start gap-2">
                <User className="w-4 h-4" />
                {formData.email}
              </p>
            </div>
          </div>
        </div>

         <RoleInvitationsPanel />

        {/* Profile Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Profile Information</h2>
          
          {/* Status Messages */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800 font-medium">Profile updated successfully!</p>
            </div>
          )}
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Mobile Number
              </label>
              <input
                type="tel"
                value={formData.mobileNumber}
                onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                placeholder="+27 XX XXX XXXX"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Globe className="w-4 h-4 inline mr-2" />
                Country
              </label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="South Africa"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
              />
            </div>

            {/* Province/State */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Province/State
              </label>
              <input
                type="text"
                value={formData.province}
                onChange={(e) => handleInputChange('province', e.target.value)}
                placeholder="KwaZulu-Natal"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
              />
            </div>

            {/* City/Town */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                City/Town
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Durban"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
              />
            </div>

            {/* Organisation IDs */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Building2 className="w-4 h-4 inline mr-2" />
                Organisation IDs
              </label>
              
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={orgInput}
                  onChange={(e) => setOrgInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddOrganisation()}
                  placeholder="Enter organisation ID"
                  className="flex-1 px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                />
                <button
                  onClick={handleAddOrganisation}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Add
                </button>
              </div>

              {formData.organisationIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.organisationIds.map((orgId) => (
                    <div
                      key={orgId}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full border border-blue-200"
                    >
                      <Building2 className="w-4 h-4" />
                      <span className="font-medium">{orgId}</span>
                      <button
                        onClick={() => handleRemoveOrganisation(orgId)}
                        className="ml-2 text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-8 pt-6 border-t border-slate-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-all font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};